/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

/**
 * Route-driven chrome. A surface declares its header (title, optional back) and its xl-and-up
 * side panel; the Shell renders them. This keeps the frame consistent while each route owns what
 * belongs in the header and the contextual right rail.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type Back = { to: string; label: string };

export type Chrome = {
  title?: ReactNode;
  back?: Back;
  sidePanel?: ReactNode;
  hideBottomNav?: boolean;
  /** The surface owns the whole viewport: no rail, header or tab bar around it. */
  fullscreen?: boolean;
};

type ChromeApi = {
  chrome: Chrome;
  setChrome: (chrome: Chrome) => void;
};

const ChromeContext = createContext<ChromeApi | undefined>(undefined);

export function ChromeProvider({ children }: { children: ReactNode }) {
  const [chrome, setChrome] = useState<Chrome>({});
  const value = useMemo(() => ({ chrome, setChrome }), [chrome]);
  return <ChromeContext.Provider value={value}>{children}</ChromeContext.Provider>;
}

/** Read the current chrome (Shell only). */
export function useChromeState(): Chrome {
  const context = useContext(ChromeContext);
  return context?.chrome ?? {};
}

/**
 * Declare this route's chrome. `deps` controls when it refreshes; the nodes are captured fresh on
 * each run so they never go stale. Cleared on unmount. A no-op outside a ChromeProvider, so routes
 * stay renderable in isolation (tests, storybook).
 */
export function useChrome(chrome: Chrome, deps: unknown[]) {
  const setChrome = useContext(ChromeContext)?.setChrome;
  const apply = useCallback(() => setChrome?.(chrome), deps);
  useEffect(() => {
    if (setChrome === undefined) {
      return;
    }
    apply();
    return () => setChrome({});
  }, [apply, setChrome]);
}
